import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { fetchMe } from "../api/me";
import { startViewAsRequest, stopViewAsRequest } from "../api/viewAs";
import { syncRolesFromMe } from "../constants/roles";
import { setViewAsUserId, clearViewAsUserId } from "../utils/viewAs";
import { syncPush } from "../utils/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [me, setMe] = useState(null);
  // AuthProvider is mounted inside QueryClientProvider (App.jsx), so it can
  // reach the cache directly — see startViewAs/stopViewAs below.
  const queryClient = useQueryClient();

  // Shared by the initial sign-in effect and by startViewAs/stopViewAs: fetch
  // /me under whatever identity the View-As header (or its absence) currently
  // implies, and update both the in-memory state and its sessionStorage cache.
  async function loadMe() {
    const meData = await fetchMe();
    setMe(meData);
    syncRolesFromMe(meData?.roles);
    try {
      sessionStorage.setItem("user", JSON.stringify(meData));
    } catch {
      /* sessionStorage throws in Safari private mode */
    }
    return meData;
  }

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser || null);
      if (!firebaseUser) {
        // Whoever signs in next on this tab must not inherit this person's
        // cached /me, query results or View As target (a leftover View As id
        // 403s every request for a non-admin). Every sign-out path lands here;
        // on a first visit with nobody signed in there is simply nothing to clear.
        try {
          sessionStorage.removeItem("user");
        } catch {
          /* sessionStorage throws in Safari private mode */
        }
        clearViewAsUserId();
        queryClient.clear();
        setMe(null);
        setLoading(false);
        return;
      }

      const cachedMe = sessionStorage.getItem("user");
      if (cachedMe) {
        const parsed = JSON.parse(cachedMe);
        setMe(parsed);
        // ⚠️ Also synced from the CACHE, not only from the fetch below. The
        // role arrays in constants/roles.js drive every management gate and
        // role picker in the app; leaving them at the three built-in literals
        // until the network round-trip lands would make a reload briefly
        // mis-render for anyone on a custom role.
        syncRolesFromMe(parsed?.roles);
      }

      try {
        // A reload mid-View-As keeps its sessionStorage flag (tab-scoped, not
        // cleared by a reload), so this correctly re-confirms the impersonated
        // identity rather than silently reverting to the admin's own.
        await loadMe();
        syncPush(); // re-link this device's notifications to whoever just signed in
      } catch (err) {
        console.error("Failed to fetch /me", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
    // queryClient is stable for the provider's lifetime, so this still runs once.
  }, [queryClient]);

  // Start "viewing as" another user — see components/ViewAsPicker.jsx. Order
  // matters: /view-as/start validates + logs BEFORE the header is attached
  // (so it runs as the admin's own identity, with no read-only conflict), the
  // header is set, THEN /me is refetched to confirm and pick up the target's
  // permissions, and only then is the whole cache cleared — every mounted
  // query is scoped by the caller's identity server-side, and none of those
  // cache keys encode who was asking, so a stale admin-scoped result would
  // otherwise flash before/instead of the target's real one.
  async function startViewAs(userId) {
    await startViewAsRequest(userId);
    setViewAsUserId(userId);
    try {
      const meData = await loadMe();
      queryClient.clear();
      return meData;
    } catch (err) {
      // Roll back rather than leave the header pointed at a session /me
      // itself couldn't confirm (e.g. a network blip right after /start).
      clearViewAsUserId();
      throw err;
    }
  }

  // Exit "viewing as" — restores the admin's own identity. `targetUserId` is
  // read from `me` BEFORE clearing the header, since `me.uid` IS the
  // impersonated target's id up to that point. The header is cleared BEFORE
  // the stop-audit call so that call is a plain, unimpersonated POST (a POST
  // made while the header is still attached would hit the read-only gate) —
  // which is exactly why it takes the target id explicitly rather than
  // relying on `user["uid"]`, which would already be back to the admin's own.
  async function stopViewAs() {
    const targetUserId = me?.uid;
    clearViewAsUserId();
    if (targetUserId) {
      try {
        await stopViewAsRequest(targetUserId);
      } catch {
        /* best effort — never block exiting on the audit call failing */
      }
    }
    const meData = await loadMe();
    queryClient.clear();
    return meData;
  }

  // Called by TermsGate right after a successful "I Agree" — updates both
  // the in-memory `me` and its sessionStorage cache so the gate in App.jsx
  // clears immediately, without waiting on (or forcing) a full /me refetch.
  function markTermsAccepted(termsAcceptedAt) {
    setMe((prev) => {
      if (!prev) return prev;
      const next = { ...prev, terms_accepted_at: termsAcceptedAt };
      try {
        sessionStorage.setItem("user", JSON.stringify(next));
      } catch {
        /* sessionStorage throws in Safari private mode */
      }
      return next;
    });
  }

  // Patch fields on the signed-in user (e.g. a new photo) without a /me refetch.
  function patchMe(fields) {
    setMe((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...fields };
      try {
        sessionStorage.setItem("user", JSON.stringify(next));
      } catch {
        /* sessionStorage throws in Safari private mode */
      }
      return next;
    });
  }

  const value = useMemo(
    () => ({
      loading,
      user,
      me,
      isAuthed: !!user,
      markTermsAccepted,
      patchMe,
      startViewAs,
      stopViewAs,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, user, me]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
